import {
  ArrowLeftOutlined,
  PauseCircleOutlined,
  PauseOutlined,
  PlayCircleOutlined,
  RedoOutlined,
  VideoCameraOutlined
} from '@ant-design/icons';
import { Alert, Button, Col, Flex, Row } from 'antd';
import RecordingRecorder from 'components/Message/CreateMessageComponent/StepMessage/RecordingRecorder/RecordingRecorder';
import { useReactMediaRecorder } from 'components/Tool/ReactMediaRecorder/ReactMediaRecorder';
import SpinComponent from 'components/Tool/SpinComponent/SpinComponent';
import { getMessageMediaConstraint } from 'constants/mediaConstraints';
import { TextToHtmlComponent } from 'components/UiKit/TextToHtmlComponent/TextToHtmlComponent';
import { forwardRef, ReactNode, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { sessionActivity } from 'utils/sessionActivity';
import { useCountDown } from 'utils/useCountDown';
import './VideoRecorder.css';

const MAX_RECORD_TIME = getMessageMediaConstraint('videoMessage').maxDurationSeconds || 120;

type AllProps = {
  scriptText?: string;
  onDone: (file: File) => void;
  onCancel: () => void;
};

function VideoRecorder({ scriptText, onDone, retake, onCancel }: AllProps & { retake: () => void }) {
  const refStart = useRef<VideoRecorderCountDownHandle>(null);
  const refResume = useRef<VideoRecorderCountDownHandle>(null);
  const refStop = useRef({ isStopped: false });

  const getExtensionFromMimeType = (mimeType: string) => {
    if (mimeType.includes('video/mp4')) return 'mp4';
    if (mimeType.includes('video/webm')) return 'webm';
    if (mimeType.includes('video/ogg')) return 'ogv';
    if (mimeType.includes('video/quicktime')) return 'mov';
    return 'mp4';
  };

  const { status, error, startRecording, stopRecording, pauseRecording, resumeRecording, previewStream, clearBlobUrl } =
    useReactMediaRecorder({
      video: true,
      audio: true,
      askPermissionOnMount: true,
      onStop: (blobUrl: string, blob: Blob) => {
        if (refStop.current.isStopped) {
          const extension = getExtensionFromMimeType(blob.type);
          onDone(new File([blob], `${new Date().getTime()}.${extension}`, { type: blob.type }));
        }
      }
    });

  const stopVideoRecording = () => {
    refStop.current.isStopped = true;
    stopRecording();
  };

  const isRecordingInProgress = status === 'recording' || status === 'paused';

  useEffect(() => {
    return () => {
      clearBlobUrl();
    };
  }, [clearBlobUrl]);

  useEffect(() => {
    sessionActivity.setRecordingActive(isRecordingInProgress);

    return () => {
      sessionActivity.setRecordingActive(false);
    };
  }, [isRecordingInProgress]);

  const handleRetake = () => {
    clearBlobUrl();
    retake();
  };

  return (
    <div className="video-recorder create-message-component-full-height">
      <SpinComponent isLoading={status === 'acquiring_media'}>
        {error ? (
          <>
            <Alert className="error-message" message={error} type="error" showIcon />
            <Button onClick={handleRetake}>Try Again</Button>
          </>
        ) : (
          <div className="recorder-container">
            <div>
              <div className="video-overlay">
                <video
                  width="100%"
                  ref={(video) => {
                    if (video) {
                      video.srcObject = previewStream;
                    }
                  }}
                  onClick={pauseRecording}
                  autoPlay
                  loop
                  playsInline
                />
                {status === 'idle' && (
                  <VideoRecorderCountDown onComplete={startRecording} ref={refStart}>
                    <PlayCircleOutlined className="icon" />
                    <p>Click to start recording</p>
                  </VideoRecorderCountDown>
                )}
                {status === 'paused' && (
                  <VideoRecorderCountDown onComplete={resumeRecording} ref={refResume}>
                    <PauseCircleOutlined className="icon" />
                    <p>Click to continue recording</p>
                  </VideoRecorderCountDown>
                )}
                <RecordingRecorder status={status} maxTime={MAX_RECORD_TIME} onTimeEnds={stopVideoRecording} />
              </div>

              <Flex gap={30} className="video-buttons" justify="center">
                <Button size="large" onClick={handleRetake}>
                  <RedoOutlined /> Retake
                </Button>

                {status === 'recording' ? (
                  <Button size="large" type="primary" onClick={pauseRecording}>
                    <PauseOutlined /> Pause / Stop
                  </Button>
                ) : (
                  <Button
                    size="large"
                    type="primary"
                    onClick={() =>
                      status === 'idle' ? refStart.current?.delayedCall() : refResume.current?.delayedCall()
                    }
                  >
                    <VideoCameraOutlined /> {status === 'idle' ? 'Start Recording' : 'Continue Recording'}
                  </Button>
                )}
              </Flex>
            </div>

            {scriptText && <TextToHtmlComponent text={scriptText} />}
          </div>
        )}
      </SpinComponent>

      <Row className="create-message-component-footer">
        <Col span={12}>
          <Button onClick={onCancel} type="link">
            <ArrowLeftOutlined /> Back
          </Button>
        </Col>

        <Col span={12} className="text-right">
          <Button
            type="primary"
            htmlType="submit"
            disabled={status !== 'paused' && status !== 'recording'}
            onClick={stopVideoRecording}
          >
            Next
          </Button>
        </Col>
      </Row>
    </div>
  );
}

function VideoRecorderWrapper(props: AllProps) {
  const [retakeCount, setRetakeCount] = useState(1);

  return <VideoRecorder key={retakeCount} {...props} retake={() => setRetakeCount((count) => count + 1)} />;
}

export default VideoRecorderWrapper;

type VideoRecorderCountDownHandle = {
  delayedCall: () => void;
};

const VideoRecorderCountDown = forwardRef<
  VideoRecorderCountDownHandle,
  { children: ReactNode; onComplete: () => void }
>(({ children, onComplete }, ref) => {
  const { timerCountDown, isCountDownStarted, startCountDown } = useCountDown();

  useImperativeHandle(
    ref,
    () => ({
      delayedCall() {
        startCountDown(3, () => onComplete());
      }
    }),
    [onComplete, startCountDown]
  );

  return (
    <div className="layer" onClick={() => startCountDown(3, () => onComplete())}>
      {isCountDownStarted ? <div className="countdown-number">{timerCountDown || ' '}</div> : children}
    </div>
  );
});
